import { ErrorDeAcceso } from '../identidad/errores';
import type {
  Administracion,
  AltaDeInversionista,
  CuentaDeInversionista,
  DatosDeInvitacion,
  PuertoDeDatos,
  SucesoDeAcceso,
} from './puerto';
import type { CifrasDelPanel } from './tipos';

/**
 * Las cifras del panel, traídas de `capital` contra el token.
 *
 * Es lo que cierra el contenido: hasta el 2026-09-21 el panel leía un archivo
 * declarado que viajaba en el bundle, así que las cifras las leía quien
 * insistiera, con sesión o sin ella.
 *
 * ## No traduce nada, a propósito
 *
 * La respuesta de `/panel/resumen` tiene **exactamente** la forma de
 * `CifrasDelPanel`, en camelCase, porque el backend tiene una prueba
 * —`test_la_forma_es_la_que_el_frontend_ya_consume`— que abre `datos/tipos.ts`
 * de este repo y falla si deja de cuadrar. Es el único punto que ata las dos
 * mitades del área, y vive en el otro repo y en la otra empresa.
 *
 * Así que aquí no hay mapeo de campos. Si algún día hiciera falta uno, la
 * pregunta correcta no es «¿dónde lo escribo?» sino «¿por qué divergieron?».
 *
 * ## El token lo pide, no lo guarda
 *
 * Se lo pide al puerto de identidad con `credencial()` en cada llamada, en vez
 * de recibirlo una vez en el constructor. Es lo que hace que una sesión
 * renovada —o cerrada— se note sin que nadie tenga que acordarse de volver a
 * construir esto.
 */
export class AdaptadorDatosHttp implements PuertoDeDatos {
  readonly esDeMentira = false;
  readonly #base: string;
  readonly #credencial: () => Promise<string | null>;

  constructor(base: string, credencial: () => Promise<string | null>) {
    this.#base = base.replace(/\/+$/, '');
    this.#credencial = credencial;
  }

  async cifras(): Promise<CifrasDelPanel> {
    return this.#pedir<CifrasDelPanel>('/panel/resumen');
  }

  async inversionistas(): Promise<Administracion> {
    const datos = await this.#pedir<{
      inversionistas: CuentaDeInversionista[];
      sucesos: SucesoDeAcceso[];
    }>('/admin/inversionistas');
    // `?? []` en los dos: un servidor anterior a la bitácora no trae `sucesos`,
    // y la pantalla tiene que seguir pintando las cuentas.
    return { cuentas: datos.inversionistas ?? [], sucesos: datos.sucesos ?? [] };
  }

  async invitar(datos: DatosDeInvitacion): Promise<AltaDeInversionista> {
    return this.#pedir<AltaDeInversionista>('/admin/inversionistas', {
      metodo: 'POST',
      // `contrasena` no se manda ni existe en `DatosDeInvitacion`: la sortea el
      // servidor. Una contraseña escrita en una petición acaba en el log de
      // acceso de cualquier proxy del camino.
      cuerpo: datos,
    });
  }

  async #pedir<T>(ruta: string, opciones?: { metodo?: string; cuerpo?: unknown }): Promise<T> {
    const token = await this.#credencial();
    if (!token) throw new ErrorDeAcceso('sesionExpirada');

    let respuesta: Response;
    try {
      respuesta = await fetch(`${this.#base}${ruta}`, {
        method: opciones?.metodo ?? 'GET',
        headers: {
          authorization: `Bearer ${token}`,
          ...(opciones?.cuerpo ? { 'content-type': 'application/json' } : {}),
        },
        body: opciones?.cuerpo ? JSON.stringify(opciones.cuerpo) : undefined,
      });
    } catch (causa) {
      // `fetch` sólo rechaza cuando no se llegó: sin red, DNS, CORS. Un 500 no
      // rechaza, llega abajo.
      throw new ErrorDeAcceso('red', causa);
    }

    if (respuesta.status === 401) throw new ErrorDeAcceso('sesionExpirada');
    // 403 y no 401: la sesión vale, lo que no alcanza es el rol. Quien lo vea
    // tiene que pedir que lo promuevan, no volver a entrar.
    if (respuesta.status === 403) throw new ErrorDeAcceso('sinPermiso');
    if (!respuesta.ok) throw new ErrorDeAcceso('servidor');

    try {
      return (await respuesta.json()) as T;
    } catch (causa) {
      // Un 200 con un cuerpo que no es JSON es un servidor que contestó mal,
      // no una red caída: distinguirlo evita mandar a alguien a mirar su wifi.
      throw new ErrorDeAcceso('servidor', causa);
    }
  }
}
